import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTheme } from '@mui/material/styles';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Link } from 'react-router-dom';
import { updateBreadcrumb } from '#action/breadcrumb';
import { fetchPaginatedUsers, changePage, clearPaginatedUsers } from '#action/user/users-paginated';
import NotFoundMessage from '#component/general/not-found-message';
import FilterWrapper from '#component/general/list-filter/filter-wrapper';
import LargeFixedButton from '#component/general/large-fixed-button';
import { useListFilterDataLoading } from '#component/general/list-filter/hooks';
import { getSearchParams } from './helper';
import filterConfig from './user-filter/config';
import { useLocalStorageSorting } from './hooks';
import UsersCardView from './card/index';
import TableView from './table/table-view';
import {
  AddBtnStyled,
  AddButtonLabel,
  Cnt,
  PaginationPlaceholder,
  StyledPageBar,
  StyledPagination
} from './styles';
import { useUrlPagination } from '#hooks';
import { setDocumentTitle } from '#helper';

/**
 * Renders users dashboard.
 * @returns {React.FC}
 */

const UsersDashboard = props => {
  const { history } = props;
  const [isUserListloading, setIsUserListloading] = useState(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(theme.breakpoints.values.XGA));
  const dispatch = useDispatch();
  const {
    users,
    page,
    pages,
    status
  } = useSelector(s => s.usersPaginated, shallowEqual);
  const filterData = useMemo(() => filterConfig, []);
  const isEmptyUsers = status === 'succeeded' && !users.length;
  const isUsersLoading = status !== 'succeeded' || status === 'idle' || isUserListloading;

  const replaceHistoryParams = useCallback(
    newParams =>
      new Promise(res => {
        const { location: { search } } = history;
        const params = getSearchParams(search, newParams);

        history.replace(`?${params}`);
        res(true);
      }),
    [history]
  );

  useUrlPagination({ page, pages, changePage });
  useLocalStorageSorting(replaceHistoryParams);

  useEffect(() => {
    setDocumentTitle('Users overview');
    dispatch(updateBreadcrumb([{ label: 'Users' }]));
  });

  const fetchData = useCallback(async () => {
    await dispatch(fetchPaginatedUsers());
    setIsUserListloading(false);
  }, [dispatch]);

  const clearData = useCallback(async () => {
    dispatch(clearPaginatedUsers());
  }, [dispatch]);

  useListFilterDataLoading(fetchData, clearData);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [page]);

  const handlePageChange = async (event, newPage) => {
    if (page !== newPage) {
      await replaceHistoryParams({ filterType: 'page', value: newPage });
      dispatch(changePage(newPage));
    }
  };

  if (isEmptyUsers) {
    return (
      <NotFoundMessage>
        <Button
          variant="contained"
          color="secondary"
          aria-label="Create user"
          component={Link}
          to="/adduser"
        >
          CREATE USER
        </Button>
      </NotFoundMessage>
    );
  }

  return (
    <section>
      <StyledPageBar>
        <FilterWrapper data={filterData} />
        {!isMobile &&
          <AddBtnStyled
            size="small"
            color="secondary"
            aria-label="Add"
            component={Link}
            to="/adduser"
          >
            <AddIcon />
            <AddButtonLabel>Add user</AddButtonLabel>
          </AddBtnStyled>}
      </StyledPageBar>
      <Cnt className="default-table">
        {isMobile ? (
          <>
            <UsersCardView
              users={users}
            />
            <LargeFixedButton directTo="/adduser" buttonName="Add user" />
          </>
        ) : (
          <TableView
            users={users}
            replaceHistoryParams={replaceHistoryParams}
          />
        )}
      </Cnt>
      {(!!pages && Number(pages) > 1 && !isUsersLoading) ? (
        <div style={isMobile ? { paddingBottom: '80px' } : {}}>
          <StyledPagination
            shape="rounded"
            color="secondary"
            count={pages}
            page={page}
            onChange={handlePageChange}
          />
        </div>
      ) : (
        <PaginationPlaceholder />
      )}
    </section>
  );
};

export default UsersDashboard;
